<template>
  <div class="container">
    <Title>Organizations</Title>

    <!-- Form to Create a New Organization -->
    <div class="card-section">
      <h3>Create Organization</h3>
      <div class="org-form">
        <div class="form-grid">
          <div>
            <label for="org-name">Organization Name:</label>
            <input type="text" id="org-name" v-model="newOrganization.name" @input="hideMissingNameError"/>
            <p v-if="showMissingNameError" class="input-error">Enter an organization name.</p>
          </div>
          <div>
            <label for="org-address">Address:</label>
            <input type="text" id="org-address" v-model="newOrganization.address"/>
          </div>
          <div>
            <label for="org-website">Website:</label>
            <input type="text" id="org-website" v-model="newOrganization.website"/>
          </div>
          <div>
            <label for="org-contact">Contact:</label>
            <input type="text" id="org-contact" v-model="newOrganization.contact"/>
          </div>
        </div>
        <button class="button-primary" @click.prevent="addOrganization">Add Organization</button>
      </div>
    </div>

    <!-- Table of Organizations -->
    <div class="card-section">
      <h3>Organizations List</h3>
      <div class="table-wrapper">
        <table>
          <thead>
          <tr>
            <th>Org ID</th>
            <th>Name</th>
            <th>Address</th>
            <th>Website</th>
            <th>Contact</th>
            <th>Status</th>
            <th>Edit</th>
            <th>Delete</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="org in organizations" :key="org.id">
            <td>{{ org.id }}</td>
            <td>
              <input v-if="isEditing && editingOrganization.id === org.id" v-model="editingOrganization.name"
                     class="table-input"/>
              <span v-else>{{ org.name }}</span>
            </td>
            <td>
              <input v-if="isEditing && editingOrganization.id === org.id"
                     v-model="editingOrganization.address" class="table-input"/>
              <span v-else>{{ org.address }}</span>
            </td>
            <td>
              <input v-if="isEditing && editingOrganization.id === org.id"
                     v-model="editingOrganization.website" class="table-input"/>
              <span v-else>{{ org.website }}</span>
            </td>
            <td>
              <input v-if="isEditing && editingOrganization.id === org.id"
                     v-model="editingOrganization.contact" class="table-input"/>
              <span v-else>{{ org.contact }}</span>
            </td>


            <td>
              <button class="status-button" :class="org.active ? 'active' : 'inactive'" @click="toggleStatus(org)">
                {{ org.active ? 'Active' : 'Inactive' }}
              </button>
            </td>
            <td>
              <button v-if="isEditing && editingOrganization.id === org.id" class="action-button save-button"
                      @click="updateOrganization">Save
              </button>
              <button v-else class="action-button edit-button" @click="editOrganization(org)">Edit</button>
            </td>
            <td>
              <button v-if="isEditing && editingOrganization.id === org.id"
                      class="action-button cancel-button" @click="cancelEdit">Cancel
              </button>
              <button v-else class="action-button delete-button" @click="confirmDelete(org.id)">Delete</button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Confirmation Dialog Box -->
    <div v-if="showDialog" class="dialog-overlay">
      <div class="dialog-box">
        <p>{{ dialogMessage }}</p>
        <div class="dialog-buttons">
          <button @click="dialogConfirm" class="button-primary">OK</button>
          <button @click="dialogCancel" class="button-secondary">
            <span>
              Cancel
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import {ref, onMounted} from 'vue';
import apiService from '@/services/apiService';
import Title from '../common/Title.vue';

const organizations = ref([]);

const newOrganization = ref({
  name: '',
  address: '',
  website: '',
  contact: '',
  active: true
});

const isEditing = ref(false);
const editingOrganization = ref({});

const showDialog = ref(false);
const dialogMessage = ref('');
const orgToDelete = ref(null);

const showMissingNameError = ref(false);

onMounted(() => {
  loadOrganizations();
});

async function loadOrganizations() {
  try {
    organizations.value = await apiService.getOrganizations();
  } catch (error) {
    console.error('Failed to fetch organizations:', error);
  }
}

async function addOrganization() {
  if (newOrganization.value.name.trim() === "") {
    showMissingNameError.value = true;
    return;
  }

  await apiService.createOrganization(
      newOrganization.value
  );

  await loadOrganizations();


  newOrganization.value = {
    name: '',
    address: '',
    website: '',
    contact: '',
    active: true
  };
}

function toggleStatus(org) {
  if (
      isEditing.value &&
      org.id === editingOrganization.value.id
  ) {
    org.active = !org.active;
    editingOrganization.value.active =
        !editingOrganization.value.active;
  }
}

function editOrganization(org) {
  isEditing.value = true;
  editingOrganization.value = {...org};
}

async function updateOrganization() {
  try {
    await apiService.updateOrganization(
        editingOrganization.value.id,
        editingOrganization.value
    );

    await loadOrganizations();
    cancelEdit();
  } catch (error) {
    console.error('Failed to update organization:', error);
    alert('Failed to update organization. Please try again.');
  }
}

function confirmDelete(orgId) {
  orgToDelete.value = orgId;
  dialogMessage.value =
      'Are you sure you want to delete this organization?';
  showDialog.value = true;
}

async function dialogConfirm() {
  if (orgToDelete.value !== null) {
    try {
      await apiService.deleteOrganization(orgToDelete.value);

      organizations.value = organizations.value.filter(
          org => org.id !== orgToDelete.value
      );
    } catch (error) {
      console.error('Failed to delete organization:', error);
      alert('Failed to delete organization. Please try again.');
    }
  }

  showDialog.value = false;
  orgToDelete.value = null;
}

function dialogCancel() {
  showDialog.value = false;
  orgToDelete.value = null;
}

function cancelEdit() {
  isEditing.value = false;
  editingOrganization.value = {};
}

function hideMissingNameError() {
  showMissingNameError.value = false;
}
</script>


<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  color: #323232;
  min-height: 90vh;
}

.card-section {
  background-color: white;
  border-radius: 6px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

h3 {
  color: #323232;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.form-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.8rem;
  align-items: start;
}

input {
  padding: 8px 10px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  height: 36px;
}

input:focus {
  outline: none;
  border-color: #505050;
}

.table-wrapper {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e8e9f5;
}

table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  font-size: 14px;
}

th,
td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #e8e9f5;
  font-weight: 500;

}

th {
  background-color: #d9e3f3;
  font-weight: 700;
  color: #323232;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

tr:last-child td {
  border-bottom: none;
}

tr:hover {
  background-color: #f9f9f9;
}

.action-button {
  min-width: 80px;
  height: 32px;
  padding: 0 12px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: 1px solid black;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.edit-button {
  background-color: #d3d3d3;
}

.edit-button:hover {
  background-color: #e8e8e8;
}

.save-button {
  background-color: #2a5ca8;
  color: white;
}

.save-button:hover {
  background-color: #1a3968;
}

.delete-button {
  background-color: #e64141;
}

.delete-button:hover {
  background-color: #f5c6cb;
}

.cancel-button {
  background-color: #e8e8e8;
  color: #323232;
}

.cancel-button:hover {
  background-color: #d8d8d8;
}

.status-button {
  min-width: 80px;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: none;
}

.status-button.active {
  background-color: #d4edda;
  color: #13294B;
}

.status-button.inactive {
  background-color: #f8d7da;
  color: #dc3545;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.dialog-box {
  background-color: white;
  padding: 1.5rem;
  border-radius: 6px;
  width: 350px;
  border: 1px solid #e8e9f5;
}

.dialog-box p {
  margin-bottom: 1rem;
  color: #323232;
}

.dialog-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 1rem;
}

.table-input {
  width: 100%;
  max-width: 150px;
  box-sizing: border-box;
}

/* New Organization Form Styles */
.org-form {
  background-color: white;
  border-radius: 6px;
  border: 0.5px solid #13294B;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.org-form label {
  display: block;
  color: #323232;
  font-size: 14px;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.org-form input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  height: 36px;
  margin-bottom: 1rem;
  box-sizing: border-box;
}

.org-form input:focus {
  outline: none;
  border-color: black;
}

/* Responsive grid for form */
@media (min-width: 768px) {
  .org-form .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}
</style>